import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Tab } from '@mui/material';

import { UserApi } from '@/api';
import { Page, PageHeader, Tabs } from '@/components';
import { IUser } from '@/models';
import { EmployeeViewTabs } from '@/pages/Employees/data';
import { EmployeeDetailsTabRoutes } from '@/routes/RoutePaths';
import { useToastStore } from '@/store';

export interface EmployeeDetailsOutletContext {
  employee?: IUser;
  refreshEmployee: () => Promise<void>;
  setEmployee: Dispatch<SetStateAction<IUser | undefined>>;
}

export const getEmployeeFullName = (employee?: IUser): string =>
  [employee?.lastName, employee?.firstName, employee?.middleName]
    .filter(Boolean)
    .join(' ')
    .trim();

const getTitle = (
  path: string,
  employee?: IUser,
  isEmployeeLoading?: boolean,
): string => {
  const employeeName = getEmployeeFullName(employee);

  switch (path) {
    case EmployeeDetailsTabRoutes.Information:
    case EmployeeDetailsTabRoutes.Devices:
      if (isEmployeeLoading) {
        return '';
      }
      return employeeName || 'View Employee';
    default:
      return '';
  }
};

export const EmployeeDetailsPage = () => {
  const { agencyId, employeeId } = useParams();

  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { updateToast } = useToastStore();
  const [employee, setEmployee] = useState<IUser>();
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(true);
  const paths = pathname.split('/');

  const activeTab = paths[paths.length - 1];

  const fetchEmployeeData = useCallback(async () => {
    setIsEmployeeLoading(true);

    if (!employeeId) {
      setEmployee(undefined);
      setIsEmployeeLoading(false);
      return;
    }

    try {
      const res = await UserApi.getOne(employeeId);
      setEmployee(res.data);
    } catch (err: any) {
      setEmployee(undefined);
      updateToast({ open: true, message: err.message });
    } finally {
      setIsEmployeeLoading(false);
    }
  }, [employeeId, updateToast]);

  useEffect(() => {
    void fetchEmployeeData();
  }, [fetchEmployeeData]);

  return (
    <Page>
      <PageHeader
        onBack={() => navigate(`/agencies/${agencyId}/employees${search}`)}
        title={getTitle(activeTab, employee, isEmployeeLoading)}
      ></PageHeader>
      <Tabs
        value={activeTab}
        onChange={(_e, newValue: string) => navigate(`${newValue}${search}`)}
      >
        {EmployeeViewTabs.map(({ path, label }) => (
          <Tab key={path} label={label} value={path} />
        ))}
      </Tabs>
      <Outlet
        context={{
          employee,
          refreshEmployee: fetchEmployeeData,
          setEmployee,
        }}
      />
    </Page>
  );
};
